import React, { useEffect, useRef } from 'react';
import { theme } from '../theme';

interface RueckgaengigToastProps {
  message: React.ReactNode;
  visible: boolean;
  // Optional. When omitted, no Rückgängig button is rendered — toast becomes a
  // plain info-toast (used for "Nothing to {action}" prompts) and auto-dismisses faster.
  onUndo?: () => void;
  onDismiss: () => void;
  durationMs?: number;
  /** Which toast this is. The timer starts when the toast appears or this key changes — never on a
   *  redraw. Omitted: a string message is its own key. */
  showKey?: string | number;
}

// Bottom-LEFT toast (operator, 2026-10-19; bottom-centred before): centred, it sat over the letter
// bar's first button on the Antworten page and over the export button on Statistik at common
// window heights. The left gutter is the one place no page puts a press. Two modes:
//   - undo mode (onUndo provided): 30 s default, shows Rückgängig button.
//   - info mode (onUndo omitted):  4 s default, no button.
export const RueckgaengigToast: React.FC<RueckgaengigToastProps> = ({
  message, visible, onUndo, onDismiss, durationMs, showKey,
}) => {
  const effectiveDuration = durationMs ?? (onUndo ? 30000 : 4000);
  // The dismiss callback is read through a ref: callers pass a fresh arrow on every render, and a
  // timer keyed to it restarted with every redraw — the lists refresh every 10 s, so a 30 s toast
  // never ran out (B5 walk, 2026-10-27: the undo toast stood for minutes).
  const onDismissRef = useRef(onDismiss);
  onDismissRef.current = onDismiss;
  const key = showKey ?? (typeof message === 'string' ? message : undefined);
  useEffect(() => {
    if (!visible) return;
    const t = setTimeout(() => onDismissRef.current(), effectiveDuration);
    return () => clearTimeout(t);
  }, [visible, effectiveDuration, key]);

  if (!visible) return null;
  return (
    <div style={{
      position: 'fixed', bottom: 24, left: 24,
      display: 'flex', alignItems: 'center', gap: 12,
      padding: '10px 16px', fontSize: 13,
      fontFamily: theme.font.family,
      backgroundColor: theme.colors.navy, color: theme.colors.white,
      borderRadius: theme.borderRadius.md,
      boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
      animation: 'stx-fadeIn 0.2s ease',
      // Above everything that can raise it (2026-09-08): CompareStage's backdrop (899) and
      // panel (900), and the page-level modals at 1000. The value comes from the deleted
      // ArchiveUndoToast, which had it right — the surviving component's own 100 would have put the
      // archive undo BEHIND the stage the clerk archived from.
      zIndex: 1100,
    }}>
      <span>{message}</span>
      {onUndo && (
        <button onClick={onUndo}
          style={{
            background: 'transparent', border: `1px solid ${theme.colors.white}`,
            color: theme.colors.white, fontSize: 12, fontWeight: 600,
            padding: '4px 10px', borderRadius: theme.borderRadius.sm,
            cursor: 'pointer', fontFamily: theme.font.family,
          }}>
          Rückgängig
        </button>
      )}
    </div>
  );
};
